import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  NgZone,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DxButtonModule } from 'devextreme-angular';
import { Subscription } from 'rxjs';
import { GisMapService } from '../../services/gis-map.service';
import { GisAddressInfo, GisEntityInfo, GisEntityName, GisLocationInfo, normalizeGisEntityName } from '../../services/gis/gis.models';

type GisMapDurum = 'lisanssiz' | 'tanimsiz' | 'aktif';

/**
 * Harita görünümü. Kendi iframe'i yoktur: yalnızca yer tutucu (slot) çizer ve GisMapService'in
 * uygulama genelindeki tek harita iframe'ini bu alanın üzerine getirmesini sağlar.
 *
 * `yanPanel` modunda (sayfa formları) harita sol menü gibi davranır: sabitlenmişse (pin) sayfada durur,
 * değilse sağ kenardaki "Harita" sekmesinden açılan kayar panelde gösterilir. Sabitken sayfadaki harita
 * başka bir şeyle (ör. formun detay paneli) örtülürse de kenar sekmesi çıkar; panel her şeyin üstünde açılır.
 */
@Component({
  selector: 'dk-gis-map',
  standalone: true,
  imports: [CommonModule, DxButtonModule],
  templateUrl: './gis-map.component.html',
  styleUrls: ['./gis-map.component.scss'],
})
export class GisMapComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  /** Harita nesnesi: MAHALLE | CADDE | BINA | KAPI | PARSEL */
  @Input() text: string;
  /** Nesnenin DaB kodu */
  @Input() value: number;
  @Input() isExternal = false;
  @Input() openPanel = false;
  @Input() width = '1000px';
  @Input() title = '';
  /** Sayfa formları için: haritayı sabitlenebilir / kenardan açılır yan panel olarak gösterir. */
  @Input() yanPanel = false;
  @Output() closePopup = new EventEmitter<any>();
  /** Haritada bir nesne seçilip "Ybs'de göster" denildiğinde (yalnızca harita bu bileşende görünürken). */
  @Output() entityInfo = new EventEmitter<GisEntityInfo>();
  /** Haritada adres/koordinat alındığında (yalnızca harita bu bileşende görünürken). */
  @Output() addressInfo = new EventEmitter<GisAddressInfo>();

  durum: GisMapDurum;
  infoText = '';
  /** Yan panel: harita sayfada sabit mi (tüm formlarda ortak tercih). */
  sabit = true;
  /** Yan panel: kenardan açılan panel açık mı. */
  panelAcik = false;

  /** Kenar sekmesi + kayar panel; formun katmanlarının üstünde kalması için body'ye taşınır. */
  @ViewChild('yanKatman') private yanKatmanRef?: ElementRef<HTMLElement>;
  private yanKatmanYeri: Comment | null = null;
  private yanKatmanTimer: ReturnType<typeof setInterval> | null = null;

  private readonly slotlar = new Map<HTMLElement, number>();
  private aktifSlotId: number | null = null;
  private ortulu = false;
  private ready = false;
  private hata: string | null = null;
  private sonBilgi: string | null = null;
  private destroyed = false;
  private readonly subs = new Subscription();

  @ViewChildren('slot') private slotRefs?: QueryList<ElementRef<HTMLElement>>;

  /** Sabit değilken sayfa içindeki harita alanı kapanır; ebeveyn düzen bu sınıfa göre genişler. */
  @HostBinding('class.yan-kapali')
  get yanKapali(): boolean {
    return this.yanPanel && !this.sabit;
  }

  constructor(
    private gisMapService: GisMapService,
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef<HTMLElement>,
    private zone: NgZone,
  ) {
    this.durum = !gisMapService.licensed ? 'lisanssiz' : !gisMapService.configured ? 'tanimsiz' : 'aktif';
  }

  /** Harita bu bileşenin yer tutucularından birinde mi gösteriliyor. */
  private get isOwner(): boolean {
    return this.aktifSlotId != null && [...this.slotlar.values()].includes(this.aktifSlotId);
  }

  /** Kenardaki "Harita" sekmesi: sabit değilken ya da sabit harita örtülmüşken. */
  get kenarSekmesi(): boolean {
    return this.yanPanel && !this.panelAcik && (!this.sabit || (this.isOwner && this.ortulu));
  }

  ngOnInit(): void {
    if (this.yanPanel) {
      this.subs.add(this.gisMapService.pinned$.subscribe(p => { this.sabit = p; this.cdr.markForCheck(); }));
    }
    if (this.durum !== 'aktif') return;
    this.subs.add(this.gisMapService.ready$.subscribe(r => { this.ready = r; if (r) this.hata = null; this.refreshInfo(); }));
    this.subs.add(this.gisMapService.activeSlotId$.subscribe(id => { this.aktifSlotId = id; this.refreshInfo(); }));
    this.subs.add(this.gisMapService.occluded$.subscribe(o => { this.ortulu = o; this.cdr.markForCheck(); }));
    this.subs.add(this.gisMapService.error$.subscribe(e => {
      if (!this.isOwner) return;
      this.hata = [e.errorCode, e.errorMessage].filter(x => x != null && x !== '').join(' / ');
      this.refreshInfo();
    }));
    this.subs.add(this.gisMapService.entityInfo$.subscribe(info => {
      if (!this.isOwner) return;
      this.sonBilgi = `Seçilen: ${info.entityName ?? ''} #${info.entityId ?? ''} — ${this.formatKonum(info)}`;
      this.refreshInfo();
      this.entityInfo.emit(info);
    }));
    this.subs.add(this.gisMapService.addressInfo$.subscribe(info => {
      if (!this.isOwner) return;
      this.sonBilgi = `Adres: ${this.formatKonum(info)}`;
      this.refreshInfo();
      this.addressInfo.emit(info);
    }));
    this.refreshInfo();
  }

  ngAfterViewInit(): void {
    this.yanKatmaniTasi();
    this.slotlariEsle();
    // change detection sırasında sahiplik/durum değişmesin diye bir sonraki mikro göreve bırakılır
    if (this.slotRefs) this.subs.add(this.slotRefs.changes.subscribe(() => Promise.resolve().then(() => this.slotlariEsle())));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['text'] || changes['value']) {
      this.slotlar.forEach(id => this.gisMapService.update(id, this.entityName, this.entityId));
      this.refreshInfo();
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.yanKatmaniGeriAl();
    this.subs.unsubscribe();
    this.slotlar.forEach(id => this.gisMapService.detach(id));
    this.slotlar.clear();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.panelAcik) this.panelKapat();
  }

  panelAc(): void {
    this.panelAcik = true;
  }

  panelKapat(): void {
    this.panelAcik = false;
  }

  /** Pin: sabitlenince panel kapanır ve harita sayfaya yerleşir; kaldırılınca sayfadaki alan kapanır. */
  sabitle(sabit: boolean): void {
    this.panelAcik = false;
    this.gisMapService.setPinned(sabit);
  }

  /**
   * Kenar sekmesi + paneli body'ye taşır (sayfa içindeki transform'lu atalar yüzünden aksi hâlde formun detay
   * panellerinin altında kalırlar). Form sekmesi gizlenince (başka sekmeye geçilince) katman da gizlenir.
   */
  private yanKatmaniTasi(): void {
    const katman = this.yanKatmanRef?.nativeElement;
    if (!katman || !katman.parentNode) return;
    this.yanKatmanYeri = document.createComment('dk-gis-map yan katman');
    katman.parentNode.insertBefore(this.yanKatmanYeri, katman);
    document.body.appendChild(katman);
    const host = this.elementRef.nativeElement;
    // Pasif form sekmeleri display:none değil visibility:hidden ile gizlenir (dx-multiview); ikisine de bakılır
    const senkron = () => {
      const gorunur = host.getClientRects().length > 0 && getComputedStyle(host).visibility === 'visible';
      katman.style.display = gorunur ? '' : 'none';
    };
    senkron();
    this.zone.runOutsideAngular(() => { this.yanKatmanTimer = setInterval(senkron, 250); });
  }

  private yanKatmaniGeriAl(): void {
    if (this.yanKatmanTimer) clearInterval(this.yanKatmanTimer);
    this.yanKatmanTimer = null;
    const katman = this.yanKatmanRef?.nativeElement;
    if (katman && this.yanKatmanYeri?.parentNode) {
      this.yanKatmanYeri.parentNode.insertBefore(katman, this.yanKatmanYeri);
    }
    this.yanKatmanYeri?.remove();
    this.yanKatmanYeri = null;
  }

  /** Şablondaki yer tutucuları (sayfa içi / panel) servise bağlar ya da ayırır. */
  private slotlariEsle(): void {
    if (this.durum !== 'aktif' || this.destroyed) return;
    const guncel = new Set((this.slotRefs?.toArray() ?? []).map(r => r.nativeElement));
    this.slotlar.forEach((id, el) => {
      if (!guncel.has(el)) {
        this.gisMapService.detach(id);
        this.slotlar.delete(el);
      }
    });
    guncel.forEach(el => {
      if (this.slotlar.has(el)) return;
      const id = this.gisMapService.attach(el, this.entityName, this.entityId);
      if (id != null) this.slotlar.set(el, id);
    });
  }

  private get entityName(): GisEntityName | null {
    return normalizeGisEntityName(this.text);
  }

  private get entityId(): number | null {
    const id = Number(this.value);
    return this.value != null && (this.value as any) !== '' && Number.isFinite(id) ? id : null;
  }

  private refreshInfo(): void {
    if (this.durum !== 'aktif') return;
    if (this.hata) this.infoText = `Harita hatası: ${this.hata}`;
    else if (!this.ready) this.infoText = 'Harita uygulaması yükleniyor, lütfen bekleyiniz..';
    else if (this.text && !this.entityName) this.infoText = `Desteklenmeyen harita nesnesi: ${this.text}`;
    else if (this.sonBilgi) this.infoText = this.sonBilgi;
    else this.infoText = 'Harita uygulaması yüklendi.';
    this.cdr.markForCheck();
  }

  private formatKonum(info: GisLocationInfo): string {
    const parcalar = [
      info.districtName,
      info.roadName,
      info.doorNumber ? `No: ${info.doorNumber}` : null,
      info.latitude != null && info.longitude != null ? `(${info.latitude}, ${info.longitude})` : null,
    ];
    return parcalar.filter(p => !!p).join(', ');
  }
}
